import * as React from 'react'
import { Download, Mail, Search, Users } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'

type Chapter = { id: number; name: string; archived: boolean }
type Member = { name: string; member_status: string; email: string | null; phone: string | null }

export function MemberPortalLeaderRoster({
  chapterId,
  chapters,
}: {
  chapterId: number
  chapters: Chapter[]
}) {
  const navigate = useNavigate()
  const chapter = chapters.find((item) => item.id === chapterId) ?? chapters[0]
  const roster = useQuery<{ members: Member[] }>({
    queryKey: ['member-portal-leader-roster', chapter?.id],
    queryFn: () => getJSON(`/api/v1/member_portal/leader/chapters/${chapter?.id}/roster`),
    enabled: Boolean(chapter),
    retry: false,
  })
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState('all')

  if (!chapter) return <EmptyState title="No chapter leadership grants found." />
  if (chapter.archived)
    return (
      <div className="mt-6 space-y-4">
        <RosterHeader
          chapter={chapter}
          chapters={chapters}
          onSelect={(id) => navigate(`/portal/leader/chapters/${id}`)}
        />
        <ArchivedChapter chapter={chapter} />
      </div>
    )
  if (roster.isLoading) return <LoadingRoster />
  if (roster.isError || !roster.data)
    return <Alert className="mt-6">Chapter roster is unavailable. Please try again.</Alert>

  const filtered = filterMembers(roster.data.members, search, status)
  const statuses = [...new Set(roster.data.members.map((member) => member.member_status))]
  return (
    <div className="mt-6 space-y-4">
      <RosterHeader
        chapter={chapter}
        chapters={chapters}
        onSelect={(id) => navigate(`/portal/leader/chapters/${id}`)}
      />
      <RosterControls
        search={search}
        status={status}
        statuses={statuses}
        onSearch={setSearch}
        onStatus={setStatus}
        chapterId={chapter.id}
      />
      {filtered.length === 0 ? (
        <EmptyState
          title="No one matches your search."
          action={
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearch('')
                setStatus('all')
              }}
            >
              Clear
            </Button>
          }
        />
      ) : (
        <RosterTable members={filtered} />
      )}
      <p className="text-caption text-muted-foreground">
        {filtered.length} of {roster.data.members.length} people
      </p>
    </div>
  )
}

function filterMembers(members: Member[], search: string, status: string) {
  const term = search.trim().toLowerCase()
  return members.filter(
    (member) =>
      (!term ||
        member.name.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term)) &&
      (status === 'all' || member.member_status === status),
  )
}

function ArchivedChapter({ chapter }: { chapter: Chapter }) {
  return (
    <p className="mt-6 text-body-sm text-muted-foreground">
      {chapter.name} is archived. Chapter tools are unavailable.
    </p>
  )
}
function LoadingRoster() {
  return (
    <p className="mt-6 text-body-sm text-muted-foreground" role="status">
      Loading chapter roster…
    </p>
  )
}

function RosterHeader({
  chapter,
  chapters,
  onSelect,
}: {
  chapter: Chapter
  chapters: Chapter[]
  onSelect: (id: string) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Users className="size-5" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-subheading font-semibold">{chapter.name}</h2>
          <p className="text-body-sm text-muted-foreground">Chapter roster</p>
        </div>
      </div>
      {chapters.length > 1 && (
        <Select value={String(chapter.id)} onValueChange={onSelect}>
          <SelectTrigger aria-label="Chapter" className="h-9 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {chapters.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

function RosterControls({
  search,
  status,
  statuses,
  onSearch,
  onStatus,
  chapterId,
}: {
  search: string
  status: string
  statuses: string[]
  onSearch: (value: string) => void
  onStatus: (value: string) => void
  chapterId: number
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative w-64">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          aria-label="Search roster"
          className="h-9 pl-8"
          placeholder="Search name or email"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>
      <Select value={status} onValueChange={onStatus}>
        <SelectTrigger aria-label="Member status" className="h-9 w-40">
          <SelectValue placeholder="Member status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {statuses.map((value) => (
            <SelectItem key={value} value={value}>
              {value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="ml-auto flex gap-2">
        <Button asChild type="button" variant="outline">
          <a href={`/api/v1/member_portal/leader/chapters/${chapterId}/roster.csv`}>
            <Download className="mr-2 size-4" aria-hidden="true" />
            Download CSV
          </a>
        </Button>
        <Button asChild type="button">
          <Link to={`/portal/leader/chapters/${chapterId}/email`}>
            <Mail className="mr-2 size-4" aria-hidden="true" />
            Email chapter
          </Link>
        </Button>
      </div>
    </div>
  )
}

function RosterTable({ members }: { members: Member[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Member status</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={`${member.name}-${member.email}`}>
              <TableCell className="py-2 text-foreground">{member.name}</TableCell>
              <TableCell className="py-2">
                <Badge
                  variant={
                    member.member_status === 'Active'
                      ? 'success'
                      : member.member_status === 'Lapsed'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {member.member_status}
                </Badge>
              </TableCell>
              <TableCell className="py-2 text-muted-foreground">{member.email}</TableCell>
              <TableCell className="py-2 text-muted-foreground">{member.phone}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
